import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Avatar,
  IconButton,
  Tooltip,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Construction,
  Carpenter,
  Plumbing,
  ElectricalServices,
  Foundation,
  DownloadOutlined,
} from '@mui/icons-material';
import {
  TRADE_TEMPLATES,
  TradeTemplate,
} from '../../../constants/tradeTemplates';

const iconMap: Record<string, React.ReactElement> = {
  Construction: <Construction />,
  Carpenter: <Carpenter />,
  Plumbing: <Plumbing />,
  ElectricalServices: <ElectricalServices />,
  Foundation: <Foundation />,
};

interface TradeTemplatePickerProps {
  selectedTemplateId: string | null;
  onSelectTemplate: (template: TradeTemplate) => void;
  siteName?: string;
}

const TradeTemplatePicker: React.FC<TradeTemplatePickerProps> = ({
  selectedTemplateId,
  onSelectTemplate,
  siteName,
}) => {
  const theme = useTheme();

  const handleDownload = (e: React.MouseEvent, template: TradeTemplate) => {
    e.stopPropagation();
    const link = document.createElement('a');
    link.href = template.csvPath;
    link.download = template.fileName;
    link.click();
  };

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: 2,
      }}
      data-testid="trade-template-picker"
    >
      {TRADE_TEMPLATES.map(template => {
        const isSelected = selectedTemplateId === template.id;

        return (
          <Paper
            key={template.id}
            onClick={() => onSelectTemplate(template)}
            sx={{
              p: 2.5,
              borderRadius: 3,
              cursor: 'pointer',
              position: 'relative',
              border: '2px solid',
              borderColor: isSelected ? 'primary.main' : 'transparent',
              bgcolor: isSelected
                ? alpha(theme.palette.primary.main, 0.04)
                : 'background.paper',
              '&:hover': {
                borderColor: isSelected ? 'primary.main' : 'divider',
              },
              transition: 'all 0.15s ease-in-out',
            }}
            data-testid={`template-${template.id}`}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
              }}
            >
              <Avatar
                sx={{
                  bgcolor: template.color,
                  width: 40,
                  height: 40,
                  mb: 1.5,
                }}
              >
                {iconMap[template.icon] || <Construction />}
              </Avatar>
              <Tooltip title={`Download ${template.name} template`}>
                <IconButton
                  size="small"
                  onClick={e => handleDownload(e, template)}
                  sx={{ mt: -0.5, mr: -0.5 }}
                >
                  <DownloadOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            <Typography variant="subtitle2" fontWeight={600}>
              {template.name}
            </Typography>

            <Typography variant="caption" color="text.secondary">
              {template.labourItems.length} items &middot;{' '}
              {siteName ? `Preview for "${siteName}"` : '10 plots'}
            </Typography>
          </Paper>
        );
      })}
    </Box>
  );
};

export default TradeTemplatePicker;
